/**
 * The product SEO block, in one place.
 *
 * `product_seo` holds five fields per storefront language: the English
 * columns and their `ar_` counterparts. The form shape (values.js) and the
 * form UI (sections/SeoSection.jsx) are both derived from this list, so a
 * field can never exist in one and be missing from the other — the failure
 * mode being a box an admin can type into whose value is silently dropped on
 * save.
 *
 * Leaving a field blank is fine and is the normal state: the storefront
 * generates that tag at render time from the product's own name, description
 * and brand, in the language of the page. Anything typed here overrides the
 * generated value for that language only — an English title never reaches the
 * Arabic page and vice versa.
 */

export const SEO_LOCALES = ["en", "ar"];

/** `("meta_title", "ar")` -> `"ar_meta_title"`; English is unprefixed. */
export const seoFieldKey = (field, locale) =>
  locale === "ar" ? `ar_${field}` : field;

/**
 * Advisory ceilings, not validation. Google truncates a title around 60
 * characters and a description around 160; going over is not an error, the
 * overflow is simply never shown. The form says so and still saves.
 */
export const SEO_TITLE_HINT_LEN = 60;
export const SEO_DESCRIPTION_HINT_LEN = 160;

export const SEO_FIELDS = [
  {
    field: "page_title",
    label: "Page Title",
    control: "input",
    hintLength: SEO_TITLE_HINT_LEN,
    en: { placeholder: "Auto: the product name" },
    ar: { placeholder: "تلقائي: اسم المنتج" },
  },
  {
    field: "meta_title",
    label: "Meta Title",
    control: "input",
    hintLength: SEO_TITLE_HINT_LEN,
    en: { placeholder: "Auto: product name + Nysmah" },
    ar: { placeholder: "تلقائي: اسم المنتج + نسمة" },
  },
  {
    field: "meta_h1_tag",
    label: "H1 Heading",
    control: "input",
    hintLength: SEO_TITLE_HINT_LEN,
    en: { placeholder: "Auto: the product name" },
    ar: { placeholder: "تلقائي: اسم المنتج" },
  },
  {
    field: "meta_h2_tag",
    label: "H2 Subheading",
    control: "input",
    hintLength: SEO_TITLE_HINT_LEN,
    en: { placeholder: "Auto: the brand (left empty when there is none)" },
    ar: { placeholder: "تلقائي: العلامة التجارية (فارغ إن لم توجد)" },
  },
  {
    field: "meta_description",
    label: "Meta Description",
    control: "textarea",
    hintLength: SEO_DESCRIPTION_HINT_LEN,
    en: { placeholder: "Auto: the start of the product description" },
    ar: { placeholder: "تلقائي: بداية وصف المنتج" },
  },
];

/** Every column name the form owns, both languages. Order matches the UI. */
export const SEO_FIELD_KEYS = SEO_FIELDS.flatMap(({ field }) =>
  SEO_LOCALES.map((locale) => seoFieldKey(field, locale)),
);

/**
 * A blank SEO block: every key present, every value an empty string.
 *
 * Every key must be present on submit. The backend treats an absent key as
 * "leave that column alone" and a present one as "write this" — so a key
 * dropped here would make the form unable to clear a field an admin had
 * emptied.
 */
export const emptySeoValues = () =>
  SEO_FIELD_KEYS.reduce((acc, key) => {
    acc[key] = "";
    return acc;
  }, {});

/**
 * Project whatever the server returned for `product.seo` into the form shape.
 *
 * Tolerant by design: the field is `{}` for a product that has never had SEO
 * saved, `null` on some responses, and — on a backend that predates the
 * Arabic columns — an object carrying only the five English keys. All three
 * must produce a complete, typeable form rather than an exception.
 */
export const seoValuesFromProduct = (seo) => {
  const source = seo && typeof seo === "object" ? seo : {};
  return SEO_FIELD_KEYS.reduce((acc, key) => {
    const value = source[key];
    // Numbers and booleans would break a controlled <input>; only a string is
    // meaningful here, and anything else is treated as "not set".
    acc[key] = typeof value === "string" ? value : "";
    return acc;
  }, {});
};

/**
 * The automatic text the storefront shows for one SEO key, or null.
 *
 * `autoSeo` is the product detail's `seo_auto`: what the page generates for
 * a blank field, from the product's saved name, description and brand. It is
 * shown greyed-out and never submitted — storing it would stop it following
 * later edits to the product. Absent on Add, and on a backend that predates
 * it, so anything that is not a non-blank string reads as "no preview".
 */
export const autoSeoText = (autoSeo, key) => {
  if (!autoSeo || typeof autoSeo !== "object") return null;
  const value = autoSeo[key];
  return typeof value === "string" && value.trim() ? value : null;
};
